'use client';

import Image from 'next/image';
import * as S from './HeadStyle';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';

type SubMenu = { label: string; value: string };
type Menu = { label: string; value: string; children?: SubMenu[] };

const MENU: Menu[] = [
    {
        label: 'ABOUT',
        value: '/about',
        children: [
            { label: 'Philosophy', value: '/about' },
            { label: 'Notice', value: '/notice' },
        ],
    },
    {
        label: 'SERVICE',
        value: '/service/network',
        children: [
            { label: 'Global Network', value: '/service/network' },
            { label: 'Our Service', value: '/service' },
            { label: 'Our Specialty', value: '/service/specialty' },
        ],
    },
    { label: 'CONTACT', value: '/contact' },
];

export default function Head() {
    const { i18n } = useTranslation();
    const pathname = usePathname();
    const router = useRouter();

    const [openKey, setOpenKey] = useState<string | null>(null); // desktop dropdown
    const [drawerOpen, setDrawerOpen] = useState(false); // mobile drawer
    const [isTouch, setIsTouch] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const locale = useMemo(() => i18n.language, [i18n.language]);
    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        localStorage.setItem('i18nextLng', lng);
    };

    useEffect(() => setIsTouch(window.matchMedia('(pointer: coarse)').matches), []);
    useEffect(() => {
        setOpenKey(null);
        setDrawerOpen(false);
    }, [pathname]);

    // ESC 닫기
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (setOpenKey(null), setDrawerOpen(false));
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // 드로어 열릴 때 배경 스크롤 잠금
    useEffect(() => {
        const original = document.documentElement.style.overflow;
        if (drawerOpen) document.documentElement.style.overflow = 'hidden';
        else document.documentElement.style.overflow = original || '';
        return () => {
            document.documentElement.style.overflow = original || '';
        };
    }, [drawerOpen]);

    const cancelClose = () => closeTimer.current && clearTimeout(closeTimer.current);
    const delayedClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpenKey(null), 220);
    };

    return (
        <S.HeadContainer>
            <S.HeaderWrapper>
                {/* 좌측: 로고 */}
                <S.HeaderLogo>
                    <Link href='/' scroll={false}>
                        <div style={{ position: 'relative', width: '220px', height: '60px' }}>
                            <Image
                                src='/image/thlogo_white.png'
                                alt='TH Logistics'
                                fill
                                style={{ objectFit: 'contain' }} // or 'cover'
                                sizes="width: '250px', height: '80px'"
                            />
                        </div>
                        {/* <Image src='/image/thlogo_new_white.png' alt='TH Logistics' width={187} height={32} priority /> */}
                    </Link>
                </S.HeaderLogo>

                {/* 가운데: 데스크톱 네비게이션 */}
                <S.Nav role='menubar' aria-label='Main'>
                    {MENU.map((menu) => {
                        const hasChildren = !!menu.children?.length;
                        const opened = openKey === menu.label;
                        return (
                            <S.MenuItem
                                key={menu.label}
                                data-open={opened || undefined}
                                className={pathname.startsWith(menu.value) ? 'active' : ''}
                                onMouseEnter={() => {
                                    if (!isTouch && hasChildren) {
                                        cancelClose();
                                        setOpenKey(menu.label);
                                    }
                                }}
                                onMouseLeave={() => {
                                    if (!isTouch && hasChildren) delayedClose();
                                }}
                            >
                                <Link
                                    href={menu.value}
                                    scroll={false}
                                    aria-haspopup={hasChildren || undefined}
                                    aria-expanded={hasChildren ? opened : undefined}
                                    onClick={(e) => {
                                        router.push(menu.value);
                                        if (hasChildren) {
                                            e.preventDefault();
                                            setOpenKey(opened ? null : menu.label);
                                        }
                                    }}
                                >
                                    {menu.label}
                                    {hasChildren && <S.Caret aria-hidden>▾</S.Caret>}
                                </Link>

                                {hasChildren && (
                                    <S.Dropdown className={opened ? 'open' : ''} role='menu' onMouseEnter={cancelClose} onMouseLeave={delayedClose}>
                                        {menu.children!.map((sub) => (
                                            <li key={sub.value} role='none'>
                                                <Link role='menuitem' href={sub.value} scroll={false} onClick={() => setOpenKey(null)}>
                                                    {sub.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </S.Dropdown>
                                )}
                            </S.MenuItem>
                        );
                    })}
                </S.Nav>

                {/* 우측: 언어 + 햄버거(모바일) */}
                <S.HeaderRight>
                    <S.HeaderLanguage>
                        <span onClick={() => changeLanguage('ko')} className={locale === 'ko' ? 'active' : undefined}>
                            KO
                        </span>
                        <span className='divider'>|</span>
                        <span onClick={() => changeLanguage('en')} className={locale === 'en' ? 'active' : undefined}>
                            EN
                        </span>
                    </S.HeaderLanguage>

                    <S.Hamburger aria-label='Open menu' aria-expanded={drawerOpen} className={drawerOpen ? 'open' : ''} onClick={() => setDrawerOpen((v) => !v)}>
                        <span />
                        <span />
                        <span />
                    </S.Hamburger>
                </S.HeaderRight>
            </S.HeaderWrapper>

            {/* 모바일 드로어 */}
            <S.DrawerOverlay className={drawerOpen ? 'show' : ''} onClick={() => setDrawerOpen(false)} />
            <S.DrawerPanel className={drawerOpen ? 'open' : ''} role='dialog' aria-modal='true' aria-hidden={!drawerOpen}>
                <div className='head'>
                    <Image src='/image/logo.png' alt='TH' width={36} height={36} />
                    <button aria-label='Close' onClick={() => setDrawerOpen(false)}>
                        ✕
                    </button>
                </div>

                <div className='list' onClick={(e) => e.stopPropagation()}>
                    {MENU.map((menu) => {
                        const hasChildren = !!menu.children?.length;
                        return (
                            <div key={menu.label} className='item'>
                                <div className='row'>
                                    <Link href={menu.value} scroll={false} onClick={() => setDrawerOpen(false)} style={{flex: "1"}}>
                                        {menu.label}
                                    </Link>
                                    {hasChildren && (
                                        <button
                                            className='acc'
                                            aria-label={`${menu.label} submenu`}
                                            aria-expanded={false}
                                            onClick={(e) => {
                                                const btn = e.currentTarget;
                                                const ul = (btn.parentElement as HTMLDivElement).nextElementSibling as HTMLUListElement;
                                                if (!ul) return;
                                                ul.classList.toggle('open');
                                                const opened = ul.classList.contains('open');
                                                btn.classList.toggle('open', opened);
                                                btn.setAttribute('aria-expanded', String(opened));
                                            }}
                                        >
                                            {/* 다운 케럿 */}
                                            <svg viewBox='0 0 24 24' className='chev' aria-hidden='true'>
                                                <path d='M6 9l6 6 6-6' />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {hasChildren && (
                                    <ul className='submenu'>
                                        {menu.children!.map((sub) => (
                                            <li key={sub.value}>
                                                <Link href={sub.value} scroll={false} onClick={() => setDrawerOpen(false)}>
                                                    {sub.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}

                    <div className='lang'>
                        <button className={locale === 'ko' ? 'active' : ''} onClick={() => changeLanguage('ko')}>
                            KO
                        </button>
                        <button className={locale === 'en' ? 'active' : ''} onClick={() => changeLanguage('en')}>
                            EN
                        </button>
                    </div>
                </div>
            </S.DrawerPanel>
        </S.HeadContainer>
    );
}
